/** @tsx tsx */
import { StatusIndicator } from "@nike/eds";
import { SearchInput } from "@nike/nike-design-system-components";
import { useContext, useMemo, useState } from "react";
import Table from "../../components/Table/Table";
import {
  ColumnTypes,
  Filters,
  RowTypes,
  TableColumn,
} from "../../components/helpers/types";
import { DateTimeCell } from "../../components/Table/DateTimeCell";
import styled from "@emotion/styled";
import { useQuery } from "@apollo/client";
import { GET_CLUSTER_INFO } from "./queries";
import { useSortedAndFilteredData } from "../../components/helpers/hooks/useSortedAndFilteredData";
import { AuthContext } from "../../auth/AuthProvider";
import { AuthState } from "@nike/aegis-auth-react/dist/types/store";

const StyledAppContainer = styled.div`
  margin: 3em auto;
  width: 90%;
`;

const SyncIndicatorContainer = styled.div`
  display: flex;
  justifycontent: center;
`;

const SearchBar = styled.div`
  margin: 0 0 1rem;
`;

const Dashboard = () => {
  const authContext = useContext(AuthContext) as AuthState;
  const { data, loading } = useQuery(GET_CLUSTER_INFO, {
    errorPolicy: "all",
    variables: {
      cluster: "content-fargate",
    },
    context: {
      headers: {
        Authorization: `Bearer ${authContext?.accessToken}`,
      }
    }
  });

  const {
    data: sortedData,
    orderBy,
    order,
    onSort,
    onFilterChange,
  } = useSortedAndFilteredData(data?.ecscluster, loading);
  const columns: TableColumn<RowTypes>[] = useMemo(
    () => [
      {
        id: ColumnTypes.APP,
        header: "Application",
        width: 200,
        valueGetter: ({ name }) => name,
        sortable: true,
        filterable: true,
      },
      {
        id: ColumnTypes.VERSION,
        header: "Version",
        width: 150,
        valueGetter: ({ version }) => version,
        sortable: true,
        filterable: true,
      },
      {
        id: ColumnTypes.CURRENT,
        header: "Current Task",
        width: 20,
        valueGetter: ({ currentTask }) => currentTask.toString(),
        sortable: true,
        filterable: false,
      },
      {
        id: ColumnTypes.LATEST,
        header: "Latest Task",
        width: 20,
        valueGetter: ({ latestTask }) => latestTask?.toString() || "",
        sortable: true,
        filterable: false,
      },
      {
        id: ColumnTypes.SYNC,
        header: "Sync",
        width: 20,
        valueGetter: ({ currentTask, latestTask }) => (
          <SyncIndicatorContainer>
            <StatusIndicator
              label=""
              size="large"
              status={currentTask !== latestTask ? "danger" : "success"}
            />
          </SyncIndicatorContainer>
        ),
        sortable: true,
        filterable: false,
      },
      {
        id: ColumnTypes.LAST,
        header: "Last Deployed",
        width: 200,
        valueGetter: ({ lastDeployed }) => <DateTimeCell date={lastDeployed} />,
        sortable: true,
        filterable: false,
      },
      {
        id: ColumnTypes.DESIRED,
        header: "Desired Count",
        width: 10,
        valueGetter: ({ desiredCount }) => desiredCount?.toString() || "",
        sortable: false,
        filterable: false,
      },
      {
        id: ColumnTypes.PENDING,
        header: "Pending Count",
        width: 10,
        valueGetter: ({ pendingCount }) => pendingCount?.toString() || "",
        sortable: false,
        filterable: false,
      },
      {
        id: ColumnTypes.RUNNING,
        header: "Running Count",
        width: 10,
        valueGetter: ({ runningCount }) => runningCount?.toString() || "",
        sortable: false,
        filterable: false,
      },
    ],
    []
  );

  const [selectedRow, setSelectedRow] = useState(null);

  return (
    <StyledAppContainer>
      <SearchBar>
        <SearchInput
          id={"search-service"}
          placeholder={"Search service"}
          crossOrigin={undefined}
          startIconAriaLabel={""}
          endIconAriaLabel={""}
          onChange={(event) => {
            console.log(event.target.value);
            onFilterChange({ name: event.target.value } as Filters);
          }}
          onEndIconClick={() => onFilterChange({ name: "" } as Filters)}
        />
      </SearchBar>
      <Table
        loading={loading}
        columns={columns}
        rows={sortedData}
        order={order}
        orderBy={orderBy}
        onSort={onSort}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
      />
    </StyledAppContainer>
  );
};

export default Dashboard;
